import i18n from 'i18n-js';
import v from 'voca';

import { MAIN_DIVISION, Roles } from '../constants';
import store from '../store';
import { parseBoolean } from './parserUtils';
import {
  isCommunityProjectAdmin,
  isCompanyAdmin,
  isDivisionAdmin,
  isMasterAdmin,
  isTecSupport
} from './authUtils';
import { sortItems } from './arrayUtils';

const RoleLabels = {
  [Roles.MASTER_ADMIN]: i18n.t('roles.masterAdmin'),
  [Roles.TEC_SUPPORT]: i18n.t('roles.tecSupport'),
  [Roles.COMPANY_ADMIN]: i18n.t('roles.companyAdmin'),
  [Roles.DIVISION_ADMIN]: i18n.t('roles.divisionAdmin'),
  [Roles.COMMUNITY_PROJECT_ADMIN]: i18n.t('roles.communityProjectAdmin'),
  [Roles.COMPANY_USER]: i18n.t('roles.companyUser'),
  [Roles.SALES_USER]: i18n.t('roles.salesUser'),
  [Roles.NOTIFICATION_USER]: i18n.t('roles.notificationUser')
};

const MAX_CUSTOM_LINKS = 3;

export function getRoleName(role) {
  return RoleLabels[role];
}

export function userIsMasterAdmin(user) {
  return user?.role === Roles.MASTER_ADMIN;
}

export function userIsTecSupport(user) {
  return user?.role === Roles.TEC_SUPPORT;
}

export function userIsCompanyAdmin(user) {
  return user?.role === Roles.COMPANY_ADMIN;
}

export function userIsDivisionAdmin(user) {
  return user?.role === Roles.DIVISION_ADMIN;
}

export function userIsCommunityProjectAdmin(user) {
  return user?.role === Roles.COMMUNITY_PROJECT_ADMIN;
}

export function userIsCompanyUser(user) {
  return user?.role === Roles.COMPANY_USER;
}

export function userIsSalesUser(user) {
  return user?.role === Roles.SALES_USER;
}

export function userIsNotificationUser(user) {
  return user?.role === Roles.NOTIFICATION_USER;
}

export function userIsMasters(user) {
  return userIsMasterAdmin(user) || userIsTecSupport(user);
}

export function userIsAdmins(user) {
  return userIsMasterAdmin(user) || userIsTecSupport(user) || userIsCompanyAdmin(user);
}

export function userHasSalesAppAccess(user) {
  return user.salesAppAccessEnabled;
}

export function userIsSales(user) {
  return userIsMasterAdmin(user)
    || userIsTecSupport(user)
    || userIsCompanyAdmin(user)
    || userIsDivisionAdmin(user)
    || userIsCommunityProjectAdmin(user)
    || userIsCompanyUser(user)
    || userIsSalesUser(user)
    || userIsNotificationUser(user);
}

export function isCurrentUser(user) {
  const { currentUser } = store.getState().users;
  return currentUser?.id === user?.id;
}

export function hasUserCompanyDomain(user) {
  return !v.isBlank(user.company?.defaultDomain);
}

export function buildProfileLink(user) {
  const domain = user.company?.defaultDomain;
  if (v.isBlank(domain)) return null;

  const path = `/profile/${user.slug}`;
  return new URL(path, domain).toString();
}

export function buildInventoryLink(user) {
  const domain = user.company?.defaultDomain;
  if (v.isBlank(domain)) return null;

  const path = `/sales/${user.slug}`;
  return new URL(path, domain).toString();
}

export function getUserFullName(firstName, lastName) {
  return [firstName, lastName].filter(Boolean).join(' ');
}

export function getUserSalutationName(user) {
  if (!v.isBlank(user.firstName)) return user.firstName;
  return user.lastName;
}

export function getSalesProfileSettings(form) {
  return {
    showSaveContactButton: parseBoolean(form.showSaveContactButton),
    showViewListingsButton: parseBoolean(form.showViewListingsButton),
    showEmailButton: parseBoolean(form.showEmailButton),
    showCallButton: parseBoolean(form.showCallButton),
    showLinkedInButton: parseBoolean(form.showLinkedInButton),
    customLinks: getCustomLinks(form)
  };
}

function getCustomLinks(form) {
  const customLinks = form.customLinks || [];
  return customLinks.map((customLink) => ({
    title: customLink.title,
    url: customLink.url,
    isActive: parseBoolean(customLink.isActive)
  }));
}

export function canAddCustomLink(customLinks) {
  return customLinks?.length < MAX_CUSTOM_LINKS;
}

export function getRoles() {
  const roles = [
    Roles.MASTER_ADMIN,
    Roles.TEC_SUPPORT,
    Roles.COMPANY_ADMIN,
    Roles.DIVISION_ADMIN,
    Roles.COMMUNITY_PROJECT_ADMIN,
    Roles.COMPANY_USER,
    Roles.SALES_USER,
    Roles.NOTIFICATION_USER,
  ];

  const rolesToExclude = [];
  if (!isMasterAdmin()) {
    if (isTecSupport()) {
      rolesToExclude.push(Roles.MASTER_ADMIN);
    } else if (isCompanyAdmin()) {
      rolesToExclude.push(...[Roles.MASTER_ADMIN, Roles.TEC_SUPPORT]);
    } else if (isDivisionAdmin()) {
      rolesToExclude.push(...[Roles.MASTER_ADMIN, Roles.TEC_SUPPORT, Roles.COMPANY_ADMIN]);
    } else if (isCommunityProjectAdmin()) {
      rolesToExclude.push(
        ...[Roles.MASTER_ADMIN, Roles.TEC_SUPPORT, Roles.COMPANY_ADMIN, Roles.DIVISION_ADMIN]
      );
    } else {
      rolesToExclude.push(...[
        Roles.MASTER_ADMIN,
        Roles.TEC_SUPPORT,
        Roles.COMPANY_ADMIN,
        Roles.DIVISION_ADMIN,
        Roles.COMMUNITY_PROJECT_ADMIN
      ]);
    }
  }

  return roles.filter((role) => !rolesToExclude.includes(role));
}

export function canSetCommunitiesProjects(form) {
  return userIsCommunityProjectAdmin(form)
    || userIsCompanyUser(form)
    || userIsSalesUser(form)
    || userIsNotificationUser(form);
}

export function canSetDivisions(form) {
  return userIsDivisionAdmin(form)
    || userIsCommunityProjectAdmin(form)
    || userIsCompanyUser(form)
    || userIsSalesUser(form)
    || userIsNotificationUser(form);
}

export function getSyncedDivisions(form) {
  if (userIsDivisionAdmin(form) && form.divisions?.length) {
    const cleaned = form.divisions.filter((d) => d.id !== MAIN_DIVISION.id);
    if (cleaned.length !== form.divisions.length) {
      return cleaned;
    }
  }

  if (canSetCommunitiesProjects(form) && form.communitiesProjects?.length) {
    const hasMainDivision = form.communitiesProjects.some(
      (cp) => cp.divisionId === MAIN_DIVISION.id
    );
    const mainDivisionAlreadyAdded = form.divisions?.some((d) => d.id === MAIN_DIVISION.id);
    if (hasMainDivision && !mainDivisionAlreadyAdded) {
      return [...(form.divisions || []), MAIN_DIVISION];
    }
  }

  return null;
}

export function mergeUserCommunitiesProjects(user) {
  const { communities = [], projects = [] } = user;
  return [
    ...communities.map((c) => ({ ...c, __typename: 'Community', key: `community-${c.id}` })),
    ...projects.map((p) => ({ ...p, __typename: 'Project', key: `project-${p.id}` }))
  ];
}

export function getInventoryCommunitiesProjects(
  communitiesProjects,
  userAssignments,
) {
  if (!userAssignments?.length) return [];

  const items = communitiesProjects.filter((communityProject) => userAssignments.some(
    (assignment) => assignment.forInventory
        && assignment.assignableId === communityProject.id
        // eslint-disable-next-line no-underscore-dangle
        && assignment.assignableType === communityProject.__typename,
  ));
  return sortItems(items, ['name']);
}
